// src/features/files/upload/useUploadQueue.js
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MAX_UPLOAD_BYTES } from '../hooks/useFiles';
import { acceptsFile } from './acceptsFile';

// Finished uploads stay on screen this long so the user sees them complete
const CLEAR_DONE_AFTER_MS = 4000;

let nextId = 0;

/**
 * Uploads files one after another, with progress per file.
 *
 * `upload(file, { onUploadProgress, meta })` uploads one file and resolves like
 * the API hooks do ({ success, data, error }). `meta` is what was passed to
 * `enqueue(files, meta)`, e.g. the folder the files were dropped into, so a file
 * still waiting goes where the user put it even if they've moved on.
 * `onUploaded(result, file, meta)` runs after each success and
 * `onFinished(results)` once the queue is empty, e.g. to reload a list once
 * instead of per file. Files over the size limit or not matching `accept` are
 * listed as failed straight away; `tooLargeMessage` replaces the default text
 * when a place has its own, smaller limit.
 *
 * Returns `items` for UploadProgressList, `enqueue(files, meta)` and `busy`.
 */
export function useUploadQueue({
  upload, onUploaded, onFinished, accept, maxBytes = MAX_UPLOAD_BYTES, tooLargeMessage,
}) {
  const { t } = useTranslation(['files', 'common']);
  const [items, setItems] = useState([]);
  const queue = useRef([]);
  const running = useRef(false);
  const clearTimer = useRef(null);

  // Latest callbacks, so a long upload doesn't call a stale closure when it ends
  const callbacks = useRef({ upload, onUploaded, onFinished });
  callbacks.current = { upload, onUploaded, onFinished };

  const update = useCallback((id, patch) => {
    setItems(prev => prev.map(item => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  const clearDone = useCallback(() => {
    setItems(prev => prev.filter(item => item.status !== 'done'));
  }, []);

  useEffect(() => () => clearTimeout(clearTimer.current), []);

  const run = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    clearTimeout(clearTimer.current);
    const results = [];
    while (queue.current.length) {
      const item = queue.current.shift();
      update(item.id, { status: 'uploading' });
      let result;
      try {
        result = await callbacks.current.upload(item.file, {
          meta: item.meta,
          onUploadProgress: (event) => {
            if (event.total) update(item.id, { progress: Math.round((event.loaded / event.total) * 100) });
          },
        });
      } catch (error) {
        result = { success: false, error: error?.message };
      }
      if (result?.success) {
        update(item.id, { status: 'done', progress: 100 });
        results.push(result);
        try {
          await callbacks.current.onUploaded?.(result, item.file, item.meta);
        } catch {
          // A failing follow-up (e.g. attaching) is reported by the caller itself
        }
      } else {
        update(item.id, { status: 'error', error: result?.error || t('common:errors.generic') });
      }
    }
    running.current = false;
    callbacks.current.onFinished?.(results);
    clearTimer.current = setTimeout(clearDone, CLEAR_DONE_AFTER_MS);
  }, [update, clearDone, t]);

  const enqueue = useCallback((files, meta) => {
    const added = Array.from(files).map((file) => {
      let error = null;
      if (file.size > maxBytes) error = tooLargeMessage || t('files:errors.file_too_large');
      else if (!acceptsFile(file, accept)) error = t('files:upload.wrong_type');
      return {
        id: `upload-${nextId++}`,
        file,
        meta,
        name: file.name,
        size: file.size,
        progress: 0,
        status: error ? 'error' : 'queued',
        error,
      };
    });
    setItems(prev => [...prev, ...added]);
    queue.current.push(...added.filter(item => item.status === 'queued'));
    run();
  }, [accept, maxBytes, tooLargeMessage, run, t]);

  const dismiss = useCallback((id) => {
    setItems(prev => prev.filter(item => item.id !== id));
  }, []);

  const clearFinished = useCallback(() => {
    setItems(prev => prev.filter(item => item.status === 'queued' || item.status === 'uploading'));
  }, []);

  const busy = items.some(item => item.status === 'queued' || item.status === 'uploading');

  return { items, enqueue, dismiss, clearFinished, busy };
}
